import { useState } from "react";
import PropTypes from "prop-types";
import { Box, Button, Grid, Stack, Typography } from "@mui/material";
import DefaultLineChart from "shared/components/Charts/LineCharts/DefaultLineChart";
import VerticalBarChart from "shared/components/Charts/BarCharts/VerticalBarChart";
import colors from "assets/theme/base/colors";
import { timelineChart, distributionCharts } from "../model/indicadoresChartsModel";

function Timeline({ summary, type, title, color, icon, description }) {
  const [offset, setOffset] = useState(0);
  const chart = timelineChart(summary, type, offset);
  return (
    <Box sx={{ minWidth: 0 }}>
      <Box sx={{ overflowX: "auto", pt: 3 }}>
        <Box sx={{ minWidth: Math.max(360, chart.labels.length * 85) }}>
          <DefaultLineChart
            height={360}
            icon={{ color, component: icon }}
            title={title}
            description={description}
            chart={{ labels: chart.labels, datasets: chart.datasets }}
          />
        </Box>
      </Box>
      <Stack direction="row" flexWrap="wrap" gap={1.5} sx={{ mt: 1, px: 1 }}>
        {chart.datasets.map((dataset) => (
          <Stack direction="row" gap={0.5} alignItems="center" key={dataset.label}>
            <Box sx={{ height: 3, width: 16, bgcolor: colors[dataset.color].main }} />
            <Typography variant="caption" color="text.secondary">
              {dataset.label}
            </Typography>
          </Stack>
        ))}
        {!chart.labels.length && (
          <Typography variant="caption" color="text.secondary">
            No hay fechas válidas para esta gráfica.
          </Typography>
        )}
      </Stack>
      {(chart.previous || chart.next) && (
        <Stack direction="row" justifyContent="flex-end">
          <Button
            size="small"
            disabled={!chart.previous}
            onClick={() => setOffset((value) => value + 1)}
          >
            Meses anteriores
          </Button>
          <Button
            size="small"
            disabled={!chart.next}
            onClick={() => setOffset((value) => value - 1)}
          >
            Meses siguientes
          </Button>
        </Stack>
      )}
    </Box>
  );
}
Timeline.propTypes = {
  summary: PropTypes.object.isRequired,
  type: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  color: PropTypes.string.isRequired,
  icon: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
};
function Distribution({ title, description, icon, color, chart, valueFormat = "number" }) {
  return (
    <Box sx={{ overflowX: "auto", pt: 3 }}>
      <Box sx={{ minWidth: Math.max(280, chart.labels.length * 90) }}>
        <VerticalBarChart
          showValues
          valueFormat={valueFormat}
          height={360}
          title={title}
          description={description}
          icon={{ color, component: icon }}
          chart={chart}
        />
      </Box>
    </Box>
  );
}
Distribution.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  icon: PropTypes.string.isRequired,
  color: PropTypes.string.isRequired,
  chart: PropTypes.object.isRequired,
  valueFormat: PropTypes.oneOf(["number", "percent"]),
};
export default function IndicadoresCharts({ summary }) {
  const charts = distributionCharts(summary);
  return (
    <Grid container spacing={3} sx={{ pt: 4 }}>
      <Grid item xs={12} lg={6}>
        <Timeline
          summary={summary}
          type="pending"
          title="Tickets Sin Resolver"
          color="warning"
          icon="pending_actions"
          description="Pendientes actuales por mes de apertura y grupo técnico"
        />
      </Grid>
      <Grid item xs={12} lg={6}>
        <Timeline
          summary={summary}
          type="resolved"
          title="Tickets Resueltos"
          color="warning"
          icon="auto_awesome"
          description="Estado actual resuelto, por mes de solución y grupo técnico"
        />
      </Grid>
      <Grid item xs={12} lg={6} sx={{ mt: 3 }}>
        <Timeline
          summary={summary}
          type="closed"
          title="Tickets Cerrados por Mes"
          color="success"
          icon="check_circle"
          description="Estado actual cerrado, agrupado por fecha de solución; no es la fecha de cierre"
        />
      </Grid>
      <Grid item xs={12} lg={6}>
        <Distribution
          title="Tickets en Curso por Grupo Técnico"
          description="Cantidad en estado En curso, sobre los filtros aplicados"
          icon="hourglass_empty"
          color="warning"
          chart={charts.enCourse}
        />
      </Grid>
      <Grid item xs={12} lg={6}>
        <Distribution
          title="Distribución de Tickets por Estado"
          description="Estado actual de todos los tickets filtrados"
          icon="bar_chart"
          color="info"
          chart={charts.states}
        />
      </Grid>
      <Grid item xs={12} lg={6}>
        <Distribution
          title="Análisis de Cumplimiento SLA"
          description="Rojo: >24 h / resueltos y cerrados con tiempo válido. Naranja: >3 días / pendientes. Sin base: sin dato."
          icon="warning"
          color="error"
          chart={charts.sla}
          valueFormat="percent"
        />
      </Grid>
    </Grid>
  );
}
IndicadoresCharts.propTypes = { summary: PropTypes.object.isRequired };
